import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Divider } from "@mui/material"
import { useTranslation } from "react-i18next";

import { SettingsEsalary, SettingsNetvisor, SettingsOpenAi, SettingsPsa, SettingsSevera } from "./settings";
import { SelectOrganization } from "../selectorganization";
import { activeIntegrationsSelector, authSelector, selectedOrganizationSelector } from "../../reducers";
import { getIntegrations } from "../../actions/organization/activeintegrations";
import styled from "styled-components";
import "./integrations.css";
import { SettingsHr } from "./modals/SettingsHr/SettingsHr.jsx";
import { SettingsProcountor } from "./modals/SettingsProcountor/SettingsProcountor.jsx";
import { SettingsTime } from "./modals/SettingsTime/SettingsTime.jsx";
import { SettingsVelhoIntegration } from "./modals/SettingsVelhoIntegration/SettingsVelhoIntegration.jsx";

export const ActivateButtonStyled = styled(Button)`
  &.btn-secondary {
    background-color: #5952e0;
    color: white;
  }

  &.btn-outline-secondary {
    border-color: #5952e0;
    color: #5952e0;
  }

  font-weight: 600;
  font-size: 13px;
  padding: 1rem;

  &.btn-disabled {
    background-color: rgba(103, 103, 103, 1);
    border-color: rgba(103, 103, 103, 1);
    color: white;
  }
`;

function ActivateButton({ onClick, isActive, isDisabled }) {
  const { t } = useTranslation();
  const disabledReason = t("Disabled because Procountor is active", "Estetty, koska Procountor on päällä");

  const ButtonComponent = (
    <ActivateButtonStyled
      disabled={isDisabled}
      onClick={onClick}
      variant={isDisabled ? "disabled" : isActive ? "secondary" : "outline-secondary"}
    >
      {isDisabled ? t("Disabled", "Ei käytössä") : isActive ? t("Activated", "Aktiivinen") : t("Activate", "Aktivoi")}
    </ActivateButtonStyled>
  );

  return (
    <Card.Footer>
      {isDisabled ? (
        <OverlayTrigger placement="top" overlay={<Tooltip id="button-tooltip">{disabledReason}</Tooltip>}>
          <span className="d-inline-block" style={{ cursor: "not-allowed", width: "100%" }}>
            {ButtonComponent}
          </span>
        </OverlayTrigger>
      ) : (
        ButtonComponent
      )}
    </Card.Footer>
  );
}

export function OrganizationIntegrations() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);
  const activeIntegrations = useSelector(activeIntegrationsSelector);

  const [settingsHr, setSettingsHr] = useState(false);
  const [settingsEsalary, setSettingsEsalary] = useState(false);
  const [settingsPsa, setSettingsPsa] = useState(false);
  const [settingsSevera, setSettingsSevera] = useState(false);
  const [settingsNetvisor, setSettingsNetvisor] = useState(false);
  const [settingsProcountor, setSettingsProcountor] = useState(false);
  const [settingsOpenAi, setSettingsOpenAi] = useState(false);
  const [settingsTime, setSettingsTime] = useState(false);
  const [settingsVelho, setSettingsVelho] = useState(false);
  const [settingsTuntivelho, setSettingsTuntivelho] = useState(false);
  const [settingsMaraplan, setSettingsMaraplan] = useState(false);

  useEffect(() => {
    if (selectedOrganization !== null && selectedOrganization.businessIdentifier) {
      dispatch(getIntegrations(selectedOrganization.businessIdentifier));
    } else {
      if (auth.companyBusinessId) {
        dispatch(getIntegrations(auth.companyBusinessId));
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }, [selectedOrganization]);

  const categoryLabels = {
    PAYROLL: t('IntegrationCategoryPayroll'),
    PROJECTMANAGEMENT: t('IntegrationCategoryProjectManagement'),
    TIMETRACKING: t('IntegrationCategoryTimeTracking'),
    OTHERS: t('IntegrationCategoryOthers'),
  };

  const integrationConfig = {
    PAYROLL: [
      {
        key: 'settingsEsalary',
        title: `Finago ${t("eSalary", "ePalkat")}`,
        img: '/integrations/heeros-epalkat.svg',
        descKey: 'DescEsalaryApi',
        descFallback: 'Ota käyttöön moderni ja automatisoitu ePalkat palkanlaskentaliittymä',
        onSettings: setSettingsEsalary,
        isDisabled: activeIntegrations.tokenProcountor,
      },
      {
        key: 'tokenNetvisor',
        title: 'Visma Netvisor',
        img: '/integrations/visma-netvisor.svg',
        descKey: 'DescNetvisorApi',
        descFallback: 'Ota käyttöön liittymä Netvisor taloushallintaohjelmistolle',
        onSettings: setSettingsNetvisor,
      },
      {
        key: 'tokenProcountor',
        title: 'Procountor',
        img: '/integrations/procountor.svg',
        descKey: 'DescProcountorApi',
        descFallback: 'Ota käyttöön liittymä Procountor taloushallinnon ohjelmistolle',
        onSettings: setSettingsProcountor,
      },
    ],
    PROJECTMANAGEMENT: [
      {
        key: 'tokenPsa',
        title: 'Finago PSA',
        img: '/integrations/heeros-psa.svg',
        descKey: 'DescPsaApi',
        descFallback: 'Ota käyttöön PSA toiminnanohjaus -liittymä',
        onSettings: setSettingsPsa,
      },
      {
        key: 'tokenSevera',
        title: 'Visma Severa',
        img: '/integrations/visma-severa.svg',
        descKey: 'DescSeveraApi',
        descFallback: 'Ota käyttöön liittymä Severa toiminnanohjausjärjestelmälle',
        onSettings: setSettingsSevera,
      },
    ],
    TIMETRACKING: [
      {
        key: 'credentialsFinagoTime',
        title: 'Time',
        img: '/integrations/finagotime.svg',
        descKey: 'DescFinagoTime',
        descFallback: 'Ota käyttöön liittymä Finago Time työajanseurantaohjelmistolle',
        onSettings: setSettingsTime,
      },
      {
        key: 'credentialsVelho',
        title: 'Finago Työvuorovelho',
        img: '/integrations/finago-työvuorovelho.png',
        descKey: 'DescVelhoApi',
        descFallback: 'Ota käyttöön liittymä Finago Työvuorovelho -ohjelmistolle',
        onSettings: setSettingsVelho,
      },
      {
        key: 'credentialsTuntivelho',
        title: 'Finago Tuntivelho',
        img: '/integrations/finago-tuntivelho.png',
        descKey: 'DescTuntivelhoApi',
        descFallback: 'Ota käyttöön liittymä Finago Tuntivelho -ohjelmistolle',
        onSettings: setSettingsTuntivelho,
      },
      {
        key: 'credentialsMaraplan',
        title: 'Finago Maraplan',
        img: '/integrations/finago-maraplan.png',
        descKey: 'DescMaraplanApi',
        descFallback: 'Ota käyttöön liittymä Finago Maraplan -ohjelmistolle',
        onSettings: setSettingsMaraplan,
      },
    ],
    OTHERS: [
      {
        key: 'tokenHr',
        title: 'Finago HR easy API',
        img: '/integrations/heeros-hr.svg',
        descKey: 'DescHrApi',
        descFallback: 'Luo sovellusliittymätunnus HR REST API:n ulkoista käyttöä varten',
        onSettings: setSettingsHr,
      },
      {
        key: 'tokenOpenAi',
        title: 'OpenAI',
        img: '/integrations/openai.svg',
        descKey: 'DescOpenAiApi',
        descFallback: 'The OpenAI API provides a simple interface to state-of-the-art AI models for natural language processing and image generation',
        onSettings: setSettingsOpenAi,
      },
    ],
  };

  return (
    <>
      {settingsHr && <SettingsHr setShow={setSettingsHr} active={activeIntegrations.tokenHr} />}
      {settingsEsalary && <SettingsEsalary setShow={setSettingsEsalary} active={activeIntegrations.settingsEsalary} />}
      {settingsPsa && <SettingsPsa setShow={setSettingsPsa} active={activeIntegrations.tokenPsa} />}
      {settingsOpenAi && <SettingsOpenAi setShow={setSettingsOpenAi} active={activeIntegrations.tokenOpenAi} />}
      {settingsSevera && <SettingsSevera setShow={setSettingsSevera} active={activeIntegrations.tokenSevera} />}
      {settingsNetvisor && <SettingsNetvisor setShow={setSettingsNetvisor} active={activeIntegrations.tokenNetvisor} />}
      {settingsTime && <SettingsTime setShow={setSettingsTime} active={activeIntegrations.credentialsFinagoTime} />}
      {settingsVelho && <SettingsVelhoIntegration setShow={setSettingsVelho} active={activeIntegrations.credentialsVelho} integrationKey="credentialsVelho" title="Finago Työvuorovelho" imgSrc="/integrations/finago-työvuorovelho.png" testIntegration="velho" />}
      {settingsTuntivelho && <SettingsVelhoIntegration setShow={setSettingsTuntivelho} active={activeIntegrations.credentialsTuntivelho} integrationKey="credentialsTuntivelho" title="Finago Tuntivelho" imgSrc="/integrations/finago-tuntivelho.png" />}
      {settingsMaraplan && <SettingsVelhoIntegration setShow={setSettingsMaraplan} active={activeIntegrations.credentialsMaraplan} integrationKey="credentialsMaraplan" title="Finago Maraplan" imgSrc="/integrations/finago-maraplan.png" />}
      {settingsProcountor && (
        <SettingsProcountor setShow={setSettingsProcountor} active={activeIntegrations.tokenProcountor} />
      )}

      <Row className="my-1">
        <Col sm="4">
          <h2 className="viewheading">{t("Integrations", "Liittymät")}</h2>
        </Col>
        <Col sm="4">
          <SelectOrganization />
        </Col>
      </Row>

      <div className="integration-container">
        {Object.entries(integrationConfig).map(([category, integrations], index, arr) => (
          <div key={category}>
            <h2 className="category-header">{categoryLabels[category]}</h2>
            <div className="integration-cards mt-3">
              {integrations.map((cfg) => (
                <Card key={cfg.key} className="integration-card">
                  <Card.Header>
                    <Card.Img src={cfg.img} />
                  </Card.Header>
                  <Card.Body>
                    <Card.Title>{cfg.title}</Card.Title>
                    <Card.Text>{t(cfg.descKey, cfg.descFallback)}</Card.Text>
                  </Card.Body>
                  <ActivateButton
                    onClick={cfg.onSettings}
                    isActive={activeIntegrations[cfg.key]}
                    isDisabled={cfg.isDisabled}
                  />
                </Card>
              ))}
            </div>
            {index < arr.length - 1 && <Divider className="mt-4 mb-4" />}
          </div>
        ))}
      </div>
    </>
  );
}
